import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { AuthService } from "app/auth/auth.service";
import SelectOption from "components/common/multi-select-dropdown/selection-option";
import { CompanyResourcesGridComponent } from "components/company-resources-grid/company-resources-grid";
import { ModalController, NavController, NavParams } from "ionic-angular";
import { LookupValue } from "models/common/lookup-value";
import Company from "models/company-details/company";
import CompanyLocation from "models/company-details/company-location";
import SortOptions from "models/content-wrapper/sort-options";
import CompanyResourceSummary from "models/resource-management/company-resource-summary";
import CompanyResourceType from "models/resource-management/company-resource-type.enum";
import ProfessionalProvider from "models/resource-management/professional-provider";
import { WorkforceContactPage } from "pages/company/workforce/workforce-contact/workforce-contact";
import { CompanyManagementService } from "providers/company-management-service/company-management-service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { VolunteerManagementServiceProvider } from "providers/volunteer-management-service/volunteer-management-service";
import { take } from "rxjs/operators";
import { AccessControlService } from "../../../../app/access-control/access-control-service";
import { Workforce } from "../../../../app/access-control/permissions";
import { PermissionsResponse } from "../../../../app/access-control/permissions-response";
import { IOrganization } from "../../../../app/auth/user.model";
import ResourceManagementLookups from "../../../../models/common/resource-management-lookups";
import { commonVar } from "../../../../providers/common-var/common";

@Component({
  selector: "workforce-resources-grid",
  templateUrl: "workforce-resources-grid.html",
})
export class WorkforceResourcesGridComponent implements OnInit, OnDestroy {
  public featureToggleProviderFlatUI: boolean;

  private _contactType: CompanyResourceType;
  private totalProviders: number;
  public providerSummaries: CompanyResourceSummary[] = [];
  sortOptions: SortOptions = new SortOptions("last_name", "asc");
  currentPage: number;
  totalPageCount: number;
  private selectedProviders: Map<number, ProfessionalProvider> = new Map<
    number,
    ProfessionalProvider
  >();
  private _languageSelectItems: SelectOption[] = [];
  query: string = "";
  nonCredentialEnabled: boolean = true;
  nonSystemUsersEnabled: boolean = true;
  showInactiveUsers: boolean = false;

  private request: any = {};

  selectedLanguages: LookupValue[] = [];
  selectedSkillsFilter: LookupValue[] = [];
  zipCodeSelected: string;
  zipCodeMask = commonVar.ZIP_CODE_MASK;

  professionalRelationSelected: LookupValue;
  disciplinesSelected: LookupValue[] = [];

  locations: IOrganization[] = [];
  locationSelected: IOrganization;

  _permissionsAccess = {} as PermissionsResponse;

  // CO-3511: fail closed. `permissionsAccess` used to be assigned here and never
  // read by anything, so the row-edit pencil below was ungated — a user holding only
  // Company:Workforce:VIEW could open any resource and rewrite it.
  /** Base Workforce edit permission, before the OPS-2858 support-user gate. */
  private canEditBase: boolean = false;

  /**
   * OPS-2858. On the Support Users tab, editing additionally requires
   * UserProfile.IsSupportUserAdmin. Everyone with Workforce view access can still
   * SEE the tab and the list - only create/edit is privileged.
   *
   * A GETTER, not a field: supportUsers changes on every tab switch while the
   * permission response arrives once, so a value computed in checkPermissions()
   * would be stale on whichever of the two happened to land second.
   *
   * Gated on the TAB rather than a per-row flag because the tab is defined by
   * mustField.supportUsers - every row on it is a support user by construction, so
   * the summary DTO does not need to carry the field.
   */
  get canEdit(): boolean {
    if (this.supportUsers === true) {
      return this.canEditBase && this.isSupportUserAdmin;
    }
    return this.canEditBase;
  }

  /** Fails closed: undefined behaves as false. */
  private get isSupportUserAdmin(): boolean {
    const user = this.authSvc.getUser();
    return !!user && user.isSupportUserAdmin === true;
  }

  @Input()
  set permissionsAccess(permissionsAccess: PermissionsResponse) {
    if (
      permissionsAccess &&
      permissionsAccess.permissions &&
      permissionsAccess.permissions.length > 0
    ) {
      this._permissionsAccess = permissionsAccess;
      this.checkPermissions();
    }
  }

  private checkPermissions() {
    this.canEditBase =
      this.accessControlService.hasAccess(
        this._permissionsAccess,
        Workforce.CompanyWorkforceEdit
      ) ||
      this.accessControlService.hasAccess(
        this._permissionsAccess,
        Workforce.CompanyWorkforceAll
      );
  }

  @ViewChild("showMore") showMore: ElementRef;

  @Input() tab: string;

  /**
   * OPS-2858 tri-state:
   *   null  - no filter. Providers and Volunteers tabs.
   *   true  - only support users. The Support Users tab.
   *   false - exclude support users. The Non-Providers tab, so a person appears
   *           in exactly one tab.
   */
  @Input() supportUsers: boolean = null;

  @Input() hasNonProviderCliniciansEnabled: boolean;

  @Input() hasProviderEnabled: boolean;

  @Input() hasVolunteersEnabled: boolean;

  @Input() rmLookups: ResourceManagementLookups;

  @Input() volunteerActivityCategories: any;

  // user: IUser | undefined;
  company: Company | undefined;

  selectedOrgType: string | undefined;

  @Input()
  set contactType(contactType: CompanyResourceType) {
    // OPS-2858: store only. The reload moved to ngOnChanges because contactType is
    // NON_PROVIDERS (4) for both the Non-Providers and Support Users tabs, so this
    // setter does not fire when switching between them - the grid would keep
    // showing the previous tab's population.
    this._contactType = contactType;
  }

  get contactType(): CompanyResourceType {
    return this._contactType;
  }

  professionalRelationOptions: LookupValue[] = [];
  private boundOnSearchIconClick: () => void;
  private searchIconEl: Element | null = null;

  constructor(
    private authSvc: AuthService,
    public navCtrl: NavController,
    public navParams: NavParams,
    public modalController: ModalController,
    public resourceManagementService: ResourceManagementService,
    public volunteerManagementService: VolunteerManagementServiceProvider,
    private navBarSvc: NavBarServiceProvider,
    private utilityService: UtilityService,
    protected companyManagementService: CompanyManagementService,
    private elementRef: ElementRef,
    private accessControlService: AccessControlService
  ) {
    this.company = authSvc.getCompany();
    this.featureToggleProviderFlatUI =
      this.navBarSvc.featureToggleOn("Provider Flat UI");
    if (this.featureToggleProviderFlatUI === undefined) {
      this.featureToggleProviderFlatUI = false;
    }
  }

  ngOnInit(): void {
    // CO-3511: this used to also call loadProfessionalProviders(0). The @Input
    // contactType setter already loads, and input binding runs before ngOnInit, so
    // every page load and every tab switch fired the same search twice.
    this.loadLocations();
  }

  ngAfterViewInit() {
    this.searchIconEl = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    if (this.searchIconEl) {
      this.boundOnSearchIconClick = this.onSearchInputChange.bind(this);
      this.searchIconEl.addEventListener("click", this.boundOnSearchIconClick);
    }
  }

  ngOnDestroy(): void {
    if (this.searchIconEl && this.boundOnSearchIconClick) {
      this.searchIconEl.removeEventListener(
        "click",
        this.boundOnSearchIconClick
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // OPS-2858: one reload when EITHER selector changes. Both change together on a
    // tab switch, and Angular delivers them in a single ngOnChanges, so this fires
    // once - keeping the CO-3511 "do not search twice" property.
    if (changes["contactType"] || changes["supportUsers"]) {
      this.clearFilters();
      this.loadProfessionalProviders(0);
    }

    if (changes["rmLookups"]) {
      if (this.rmLookups && this.rmLookups.ProfessionalRelationType) {
        this.professionalRelationOptions =
          this.rmLookups.ProfessionalRelationType.filter(
            (item) => item.description !== "Auditor"
          );
      }
    }
  }

  loadLocations() {
    this.utilityService.increaseSpinnerCount();
    this.loadFilters();

    this.locations = [];
    this.companyManagementService.getCompanyLocations(this.request).subscribe(
      (response) => {
        if (response && response["hits"] && response["totalHits"] > 0) {
          let hits = response["hits"] as CompanyLocation[];
          this.locations = hits.map((x) => {
            return {
              abbreviation: x.displayCode,
              orgId: x.locationId,
              orgName: x.name,
              orgType: x.entityType,
              orgTimezone: x.timezone,
              roles: [],
            } as IOrganization;
          });
          let iOrganization = this.authSvc
            .getUser()
            .organizations.find((x) => x.orgId === hits[0].companyId);
          // CO-3511: this was unshifted unguarded, so a user whose organization list
          // does not contain the company got an `undefined` entry in the location
          // filter, which renders as a blank selectable option.
          if (iOrganization) {
            this.locations.unshift(iOrganization);
          }
        }
        this.utilityService.decreaseSpinnerCount();
      },
      () => this.utilityService.decreaseSpinnerCount(),
      () => this.utilityService.decreaseSpinnerCount()
    );
  }

  loadFilters() {
    this.request = {};
    this.request.entityType = "LOCATION";
    this.request.size = 200;
    this.request.mustFields = [
      {
        name: "pk",
        value: `COMPANY#${this.company.companyId}`,
      },
    ];
    this.request.sortBy = {
      name: "name",
      value: "asc",
    };
  }

  showMoreLocations(p: CompanyResourceSummary) {
    p.showMore = !p.showMore;
  }

  openCompanyResource(p: CompanyResourceSummary) {
    // CO-3511: boundary guard. The template hides the pencil without the edit
    // permission, but hiding a control is a hint, not a gate — this is the gate.
    if (!this.canEdit) {
      return;
    }

    this.utilityService.increaseSpinnerCount(
      `Editing ${this.getContactType()} ...`
    );
    this.resourceManagementService
      .getCredentialedResource(p.id)
      .pipe(take(1))
      .subscribe(
        (x) => {
          this.navCtrl.push(WorkforceContactPage, {
            provider: x,
            languagesSelectItems: this._languageSelectItems,
            volunteerTypes: this.rmLookups.VolunteerType,
            parent: this,
            isNew: false,
            isEdit: true,
            // CO-3511: this was hardcoded to 1 (Provider) regardless of which tab
            // the row came from, so the edit page briefly configured itself as a
            // Provider before loadData() corrected it from the resource payload.
            contactType: this.contactType,
            rmLookups: this.rmLookups,
          });
          this.utilityService.decreaseSpinnerCount();
        },
        () => this.utilityService.decreaseSpinnerCount(),
        () => this.utilityService.decreaseSpinnerCount()
      );
  }

  onSearchInputChange() {
    this.loadProfessionalProviders(0);
  }

  onSearchCancel() {
    this.clearFilters();
    this.loadProfessionalProviders(0);
  }

  onFilter() {
    this.loadProfessionalProviders(0);
  }

  onPageChange(selectedPage: number) {
    this.loadProfessionalProviders(selectedPage);
  }

  loadProfessionalProviders(selectedPage: number): void {
    this.utilityService.increaseSpinnerCount(
      `Loading ${this.getContactType()} ...`
    );
    let location = this.locationSelected ? this.locationSelected.orgId : "";
    this.selectedOrgType = this.authSvc.getUser().selectedOrgType;
    let companyId = 0;
    if (this.selectedOrgType && this.selectedOrgType === "COMPANY") {
      companyId = +this.authSvc.getCompany().companyId;
    }
    if (this.contactType === 5) {
      this.volunteerManagementService
        .getWorkforceVolunteersFilter(
          selectedPage,
          CompanyResourcesGridComponent.LIMIT,
          encodeURIComponent(this.query),
          this.sortOptions,
          this.selectedSkillsFilter.map((x: LookupValue) => x.description),
          this.zipCodeSelected,
          this.selectedLanguages.map((x: LookupValue) => x.id),
          this.nonCredentialEnabled,
          location,
          this.professionalRelationSelected,
          this.showInactiveUsers
        )
        .pipe()
        .take(1)
        .subscribe(
          (x) => {
            this.providerSummaries = x.content;
            this.totalPageCount = x.totalPages;
            this.totalProviders = x.totalElements;
            this.currentPage = x.pageable.pageNumber;
            this.utilityService.decreaseSpinnerCount();
          },
          () => this.utilityService.decreaseSpinnerCount(),
          () => this.utilityService.decreaseSpinnerCount()
        );
    } else {
      this.resourceManagementService
        .getWorkforceNonVolunteerFilter(
          selectedPage,
          CompanyResourcesGridComponent.LIMIT,
          encodeURIComponent(this.query),
          this.sortOptions,
          this.contactType,
          "",
          this.nonCredentialEnabled,
          !this.nonSystemUsersEnabled,
          location,
          this.professionalRelationSelected,
          companyId,
          this.showInactiveUsers,
          this.disciplinesSelected,
          this.supportUsers
        )
        .pipe()
        .take(1)
        .subscribe(
          (x) => {
            this.providerSummaries = x.content;
            this.totalPageCount = x.totalPages;
            this.totalProviders = x.totalElements;
            this.currentPage = x.pageable.pageNumber;
            this.utilityService.decreaseSpinnerCount();
          },
          () => this.utilityService.decreaseSpinnerCount(),
          () => this.utilityService.decreaseSpinnerCount()
        );
    }
  }

  getContactType() {
    let resource = "providers";
    if (this.supportUsers === true) {
      // OPS-2858: checked first - support users are contact type 4 underneath, so
      // testing contactType alone would label them "non-provider clinicians".
      resource = "support users";
    } else if (this.contactType === 4) {
      resource = "non-provider clinicians";
    } else if (this.contactType === 5) {
      resource = "volunteers";
    }
    return resource;
  }

  arrowClick(e, p: CompanyResourceSummary) {
    e.stopPropagation();
    p.isSelected = !p.isSelected;
    if (p.isSelected && p.id) {
      if (!this.selectedProviders.has(p.id)) {
        p.$provider = this.resourceManagementService.getCredentialedResource(
          p.id
        );
      }
    }
  }

  sortBy(selectedCol) {
    this.currentPage = 0;
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }

    this.loadProfessionalProviders(0);
  }

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  clearFilters() {
    this.query = "";
    this.nonCredentialEnabled = true;
    this.nonSystemUsersEnabled = true;
    this.sortOptions = new SortOptions("last_name", "asc");
    this.selectedSkillsFilter = [];
    this.locationSelected = null;
    this.zipCodeSelected = "";
    this.selectedLanguages = [];
    this.professionalRelationSelected = null;
    // CO-3511: this was `null`, unlike every sibling filter and unlike its own
    // declaration (`LookupValue[] = []`), so the multi-select was cleared to a
    // different empty than the one it started with.
    this.disciplinesSelected = [];
  }

  checkClearInput() {
    if (!this.query || this.query.trim().length === 0) {
      this.loadProfessionalProviders(0);
    }
  }

  clearSelection(type: number) {
    if (type === 1) this.selectedSkillsFilter = [];
    else this.selectedLanguages = [];
  }

  get resourceIsVolunteer(): boolean {
    return this.contactType === CompanyResourceType.VOLUNTEER;
  }

  onToggleNonCredentialEnabled() {
    this.onSearchInputChange();
  }

  onToggleNonSystemUsersEnabled() {
    this.onSearchInputChange();
  }

  onToggleShowInactive() {
    this.onSearchInputChange();
  }

  sortByDiscipline() {
    if (!this.disciplinesSelected || this.disciplinesSelected.length === 0) {
      this.sortOptions.column = "last_name";
      this.sortOptions.direction = "asc";
      return;
    }
    if (this.disciplinesSelected && this.disciplinesSelected.length > 0) {
      this.sortOptions.column = "discipline";
      this.sortOptions.direction = "asc";
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/workforce/workforce-resources-grid/workforce-resources-grid.ts